import { Typography } from "@mui/material";
import { enqueueSnackbar } from "@powerdiary/components-library/lib/components/Snackbar";
import { AxiosError } from "axios";
import { parseDigits } from "libphonenumber-js";
import { BaseSyntheticEvent, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useClientPortalConfigContext } from "../../../../context";
import { is400Error, usePostMutation } from "../../../../hooks/useClientPortalQuery";
import {
    AccountModel,
    RecoverPasswordModel,
    ResendVerificationCodeModel,
    ResetPasswordFailedApiDetailsErrorResponse,
} from "../../../../types/generated/models";
import { formatPhoneNumber, RegisterType, Step } from "./ResetPasswordForm.helpers";
import { LoginDialogMode } from "../../Account.types";

export interface IUserRegisterHook {
    isPending: boolean;
    isResending: boolean;
    sentViaEmail: boolean;
    register: (values: RegisterType, event: BaseSyntheticEvent) => Promise<void>;
    resendVerificationCode: () => Promise<void>;
}

export function useResetPassword(
    form: UseFormReturn<RegisterType>,
    setMode: (mode: LoginDialogMode) => void
): IUserRegisterHook {
    const { clientPortalGuid, clientPortalConfig } = useClientPortalConfigContext();
    const { getValues, setValue, reset, setError, clearErrors } = form;
    const [sentViaEmail, setSentViaEmail] = useState(false);

    const url = `/api/v1/${clientPortalGuid}/Auth/GetToken`;
    const resendUrl = `/api/v1/${clientPortalGuid}/Auth/ResendVerificationCode`;

    const { mutateAsync, isPending } = usePostMutation<AccountModel, AxiosError, RecoverPasswordModel>({ url });
    const { mutateAsync: resendMutateAsync, isPending: isResending } = usePostMutation<
        void,
        AxiosError,
        ResendVerificationCodeModel
    >({ url: resendUrl });

    const register = async (values: RegisterType, event: BaseSyntheticEvent) => {
        if (values.phoneNumber) {
            const countryCode = clientPortalConfig.countryCode;
            const formattedMobileNumber = formatPhoneNumber(values.phoneNumber, countryCode);
            values.phoneNumber = parseDigits(formattedMobileNumber);
        }
        try {
            const response = await mutateAsync(values as RecoverPasswordModel);

            if (values.step === Step.Initial) {
                setSentViaEmail(response.sentViaEmail === true);
                setValue("step", Step.Verification);
                event.preventDefault();
            } else if (values.step === Step.Verification) {
                setValue("step", Step.PasswordReset);
                event.preventDefault();
            } else {
                setMode(LoginDialogMode.Login);
                enqueueSnackbar(<Typography variant="body2">Your password has been updated.</Typography>, {
                    variant: "success",
                    alertTitle: "Password reset successful",
                });
            }
        } catch (error) {
            const typedError = error as AxiosError<ResetPasswordFailedApiDetailsErrorResponse>;
            if (is400Error(typedError)) {
                if (typedError.response?.data?.details?.tokenError && values.step === Step.Initial) {
                    setValue("step", Step.Verification);
                }
                setError("root", {
                    type: "manual",
                    message: typedError.response?.data?.message || "An error occurred during registration.",
                });
            }
        } finally {
            const formValues = getValues();
            reset(formValues, {
                keepErrors: true,
            });
        }
    };

    const resendVerificationCode = async () => {
        const values = getValues();
        const countryCode = clientPortalConfig.countryCode;

        const resendData: ResendVerificationCodeModel = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNumber: values.phoneNumber ? parseDigits(formatPhoneNumber(values.phoneNumber, countryCode)) : null,
        };

        try {
            await resendMutateAsync(resendData);
            clearErrors("root");
            enqueueSnackbar(<Typography variant="body2">Verification code has been resent.</Typography>, {
                variant: "success",
                alertTitle: "Code resent",
            });
        } catch (error) {
            const typedError = error as AxiosError<ResetPasswordFailedApiDetailsErrorResponse>;
            if (is400Error(typedError)) {
                setError("root", {
                    type: "manual",
                    message:
                        typedError.response?.data?.message ||
                        "An error occurred while resending the verification code.",
                });
            }
        }
    };

    return {
        isPending,
        isResending,
        sentViaEmail,
        register,
        resendVerificationCode,
    };
}
